"use client";

import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import CheckCircle from "lucide-react/dist/esm/icons/circle-check";
import Mail from "lucide-react/dist/esm/icons/mail";
import { trackEmailAppOpened } from "@/lib/analytics/events";
import type { SignupSurface } from "@/lib/analytics/events";

/**
 * Map a recipient's email domain to its webmail URL + a friendly provider label
 * so we can deep-link "Open <Provider>" straight to their inbox. Kept here as
 * the single source of truth — both the signup page and the express dialog
 * previously inlined their own copies of this map.
 */
const PROVIDERS: Record<string, { url: string; label: string; provider: string }> = {
  "gmail.com": { url: "https://mail.google.com", label: "Gmail", provider: "gmail" },
  "googlemail.com": { url: "https://mail.google.com", label: "Gmail", provider: "gmail" },
  "outlook.com": { url: "https://outlook.live.com", label: "Outlook", provider: "outlook" },
  "hotmail.com": { url: "https://outlook.live.com", label: "Outlook", provider: "outlook" },
  "live.com": { url: "https://outlook.live.com", label: "Outlook", provider: "outlook" },
  "yahoo.com": { url: "https://mail.yahoo.com", label: "Yahoo Mail", provider: "yahoo" },
  "icloud.com": { url: "https://www.icloud.com/mail", label: "iCloud Mail", provider: "icloud" },
  "me.com": { url: "https://www.icloud.com/mail", label: "iCloud Mail", provider: "icloud" },
  "protonmail.com": { url: "https://mail.proton.me", label: "Proton Mail", provider: "proton" },
  "proton.me": { url: "https://mail.proton.me", label: "Proton Mail", provider: "proton" },
};

function resolveProvider(email: string): { url: string; label: string; provider: string } {
  const domain = email.split("@")[1]?.toLowerCase();
  if (!domain) {
    return { url: "https://mail.google.com", label: "mail", provider: "unknown" };
  }
  return PROVIDERS[domain] ?? { url: `https://${domain}`, label: "mail", provider: domain };
}

interface CheckYourEmailScreenProps {
  email: string;
  surface: SignupSurface;
  /** Verb describing what auto-resumes after verification (e.g. "download"). */
  actionLabel?: string;
  /** Slot for a resend-verification control. */
  resendSlot?: ReactNode;
  /** Slot for trailing content (referral teaser, Discord banner, etc.). */
  footer?: ReactNode;
}

/**
 * Shared "check your email" success screen for every signup surface. Renders a
 * success state with a one-tap "Open <Provider>" button that deep-links to the
 * recipient's webmail, plus slots for resend + a custom footer.
 */
export function CheckYourEmailScreen({
  email,
  surface,
  actionLabel,
  resendSlot,
  footer,
}: CheckYourEmailScreenProps) {
  const { url, label, provider } = resolveProvider(email);

  const handleOpenMail = () => {
    trackEmailAppOpened(provider, surface);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-5 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success/10">
        <CheckCircle className="h-7 w-7 text-success" />
      </div>

      <div className="space-y-2">
        <h2 className="text-lg font-semibold text-foreground">Check your email</h2>
        <p className="text-sm text-muted-foreground">
          We sent a verification link to <strong className="text-foreground">{email}</strong>.
        </p>
        <p className="text-sm text-muted-foreground">
          Your {actionLabel ?? "action"} will start automatically once you verify.
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <Button className="w-full h-11 font-semibold" onClick={handleOpenMail}>
          <Mail className="h-4 w-4" />
          Open {label}
        </Button>
        {resendSlot}
      </div>

      {footer}
    </div>
  );
}
